import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import OverviewPage from '../overview/OverviewPage';
import { useOperations } from '../operations/OperationsProvider';
import useOperationalPermissions from '../common/util/operationalPermissions';

/**
 * Where a person lands when they sign in.
 *
 * Somebody posted to a gate spends the shift admitting trucks, and somebody posted to a bay spends
 * it receiving them; neither opens the plant overview first. So the stage they are posted to
 * decides the page, and the overview stays what it was for everyone else.
 *
 * It redirects once, on arrival, rather than owning "/" permanently: a gate clerk who later clicks
 * Today means the overview, and should get it.
 */
const ARRIVED = 'app:landed';

// The stages a desk works at, and the page each one's work is done on.
const HOME = {
  GATE_IN: '/gate-operations',
  GATE_OUT: '/gate-operations',
  SECURITY: '/gate-operations',
  BAY: '/yard-intake',
  WEIGHBRIDGE: '/yard-intake',
};

const read = () => { try { return window.sessionStorage.getItem(ARRIVED); } catch { return '1'; } };
const mark = () => { try { window.sessionStorage.setItem(ARRIVED, '1'); } catch { /* not worth failing over */ } };

const Landing = () => {
  const user = useSelector((state) => state.session.user);
  const { haulier } = useOperationalPermissions();
  const { data } = useOperations();
  const posted = data?.checkpoints?.find((c) => c.id === user?.postedCheckpointId);

  // A haulier account belongs to the company that owns the trucks, not to the plant. Its navigation
  // is two sections - where its trucks are, and the trucks themselves - and the plant overview is in
  // neither, so signing in used to land it on a page it could not have reached from its own menu.
  if (haulier) return <Navigate to="/live" replace />;

  // Checkpoints arrive with the operations feed, so the first render of a fresh session usually has
  // none yet. Waiting for them would blank the page; showing the overview and redirecting when they
  // land would move the page under somebody already reading it. So a session that has not been
  // placed yet is simply left on the overview until it has something to place it by.
  if (read() || !user?.postedCheckpointId) return <OverviewPage />;
  if (!data?.checkpoints?.length) return <OverviewPage />;
  mark();
  const home = HOME[posted?.type];
  return home ? <Navigate to={home} replace /> : <OverviewPage />;
};

export default Landing;
